import React, { useEffect, useState, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend,
  ChartOptions,
  ChartData
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
// HTML Legend Plugin based on Chart.js documentation
const getOrCreateLegendList = (chart: any, id: string) => {
  const legendContainer = document.getElementById(id);
  let listContainer = legendContainer?.querySelector('ul');
  if (!listContainer) {
    listContainer = document.createElement('ul');
    listContainer.style.display = 'flex';
    listContainer.style.flexDirection = 'row'; // Horizontal layout like other charts
    listContainer.style.flexWrap = 'nowrap'; // Prevent wrapping to second row
    listContainer.style.overflow = 'hidden'; // Hide overflow items
    listContainer.style.margin = '0';
    listContainer.style.padding = '0';
    listContainer.style.marginBottom = '20px'; // Bottom margin for spacing
    legendContainer?.appendChild(listContainer);
  }
  return listContainer;
};

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart: any, args: any, options: any) {
    const ul = getOrCreateLegendList(chart, options.containerID);
    // Remove old legend items
    while (ul.firstChild) {
      ul.firstChild.remove();
    }
    // Reuse the built-in legendItems generator
    const items = chart.options.plugins.legend.labels.generateLabels(chart);
    
    // Calculate how many items can fit based on actual legend item width
    const containerWidth = ul.parentElement?.offsetWidth || 800;
    const estimatedItemWidth = 180; // More accurate width per legend item
    const maxItemsPerRow = Math.floor(containerWidth / estimatedItemWidth);
    const itemsPerRow = Math.max(3, Math.min(maxItemsPerRow, items.length)); // At least 3 items
    const itemsToShow = options.showAllLegends ? items.length : Math.min(itemsPerRow, items.length);
    const hasMoreItems = items.length > itemsPerRow;
    
    items.slice(0, itemsToShow).forEach((item: any) => {
      const li = document.createElement('li');
      li.style.alignItems = 'center';
      li.style.cursor = 'pointer';
      li.style.display = 'flex';
      li.style.flexDirection = 'row';
      li.style.marginRight = '15px'; // Horizontal spacing between legend items
      li.onclick = () => {
        const {type} = chart.config;
        if (type === 'pie' || type === 'doughnut') {
          chart.toggleDataVisibility(item.index);
        } else {
          chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
        }
        chart.update();
      };
      // Color box - CIRCULAR for pie chart
      const boxSpan = document.createElement('span');
      boxSpan.style.background = item.hidden ? '#ddd' : item.fillStyle; // Use fillStyle for pie chart
      boxSpan.style.borderRadius = '50%'; // Make it circular
      boxSpan.style.display = 'inline-block';
      boxSpan.style.flexShrink = '0';
      boxSpan.style.height = '15px';
      boxSpan.style.marginRight = '8px';
      boxSpan.style.width = '15px';
      boxSpan.style.opacity = item.hidden ? '0.5' : '1';
      // Text
      const textContainer = document.createElement('p');
      textContainer.style.color = item.hidden ? '#aaa' : item.fontColor;
      textContainer.style.margin = '0';
      textContainer.style.padding = '0';
      textContainer.style.textDecoration = item.hidden ? 'line-through' : 'none';
      textContainer.style.fontFamily = 'Inter';
      textContainer.style.fontWeight = '400';
      textContainer.style.fontStyle = 'Regular';
      textContainer.style.fontSize = '13px';
      textContainer.style.lineHeight = '18px';
      textContainer.style.letterSpacing = '0.32px';
      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);
      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      ul.appendChild(li);
    });
    
    // Add "See more" button if there are more items
    if (hasMoreItems && !options.showAllLegends) {
      const seeMoreLi = document.createElement('li');
      seeMoreLi.style.alignItems = 'center';
      seeMoreLi.style.cursor = 'pointer';
      seeMoreLi.style.display = 'flex';
      seeMoreLi.style.flexDirection = 'row';
      seeMoreLi.style.marginRight = '15px';
      seeMoreLi.style.padding = '4px 8px';
      seeMoreLi.style.borderRadius = '9999px'; // Rounded full like AppliedFiltersDisplay
      seeMoreLi.style.backgroundColor = 'transparent';
      seeMoreLi.style.fontSize = '14px';
      seeMoreLi.style.color = '#6b7280'; // text-tertiary-text color
      seeMoreLi.style.fontFamily = 'Inter';
      seeMoreLi.style.transition = 'background-color 0.2s';
      seeMoreLi.style.gap = '4px';
      
      // Add hover effect - match AppliedFiltersDisplay exactly
      seeMoreLi.onmouseenter = () => {
        seeMoreLi.style.backgroundColor = '#e6e6e6'; // hover:bg-[#e6e6e6]
      };
      seeMoreLi.onmouseleave = () => {
        seeMoreLi.style.backgroundColor = 'transparent';
      };
      
      seeMoreLi.onclick = () => {
        if (options.onToggleShowMore) {
          options.onToggleShowMore();
        }
      };
      
      // Create text and chevron icon
      const hiddenCount = items.length - itemsPerRow;
      const seeMoreText = document.createTextNode(`See more (${hiddenCount})`);
      seeMoreLi.appendChild(seeMoreText);
      
      // Add chevron down icon (simple SVG)
      const chevronIcon = document.createElement('span');
      chevronIcon.innerHTML = '▼';
      chevronIcon.style.fontSize = '10px';
      chevronIcon.style.marginLeft = '4px';
      seeMoreLi.appendChild(chevronIcon);
      
      ul.appendChild(seeMoreLi);
    } else if (hasMoreItems && options.showAllLegends) {
      // Add "See less" button when all legends are shown
      const seeLessLi = document.createElement('li');
      seeLessLi.style.alignItems = 'center';
      seeLessLi.style.cursor = 'pointer';
      seeLessLi.style.display = 'flex';
      seeLessLi.style.flexDirection = 'row';
      seeLessLi.style.marginRight = '15px';
      seeLessLi.style.padding = '4px 8px';
      seeLessLi.style.borderRadius = '9999px'; // Rounded full like AppliedFiltersDisplay
      seeLessLi.style.backgroundColor = 'transparent';
      seeLessLi.style.fontSize = '14px';
      seeLessLi.style.color = '#6b7280'; // text-tertiary-text color
      seeLessLi.style.fontFamily = 'Inter';
      seeLessLi.style.transition = 'background-color 0.2s';
      seeLessLi.style.gap = '4px';
      
      // Add hover effect - match AppliedFiltersDisplay exactly
      seeLessLi.onmouseenter = () => {
        seeLessLi.style.backgroundColor = '#e6e6e6'; // hover:bg-[#e6e6e6]
      };
      seeLessLi.onmouseleave = () => {
        seeLessLi.style.backgroundColor = 'transparent';
      };
      
      seeLessLi.onclick = () => {
        if (options.onToggleShowMore) {
          options.onToggleShowMore();
        }
      };
      
      // Create text and chevron icon
      const seeLessText = document.createTextNode('See less');
      seeLessLi.appendChild(seeLessText);
      
      // Add chevron up icon (simple SVG)
      const chevronIcon = document.createElement('span');
      chevronIcon.innerHTML = '▲';
      chevronIcon.style.fontSize = '10px';
      chevronIcon.style.marginLeft = '4px';
      seeLessLi.appendChild(chevronIcon);
      
      ul.appendChild(seeLessLi);
    }
  }
};

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, htmlLegendPlugin);

const ExternalLabelsPlugin = {
  id: 'externalLabels',
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);
    
    if (!dataset || !meta) return;
    
    const total = dataset.data.reduce((sum: number, value: number) => sum + value, 0);
    
    meta.data.forEach((element: any, index: number) => {
      const { x, y } = element.getProps(['x', 'y'], true);
      const startAngle = element.startAngle;
      const endAngle = element.endAngle;
      const midAngle = (startAngle + endAngle) / 2;
      
      // Calculate position outside the pie
      const radius = element.outerRadius + 30;
      const labelX = x + Math.cos(midAngle) * radius;
      const labelY = y + Math.sin(midAngle) * radius;
      
      // Get data values
      const value = dataset.data[index];
      const percentage = ((value / total) * 100).toFixed(1);
      const label = chart.data.labels[index];
      
      // Only show labels for segments > 5%
      if (parseFloat(percentage) <= 5) return;
      
      // Style the text
      ctx.fillStyle = '#374151';
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = labelX > x ? 'left' : 'right';
      ctx.textBaseline = 'middle';
      
      // Draw the label with percentage
      const text = `${label}: ${percentage}%`;
      ctx.fillText(text, labelX, labelY);
      
      // Draw connecting line
      ctx.strokeStyle = '#9CA3AF';
      ctx.lineWidth = 1;
      ctx.beginPath();
      const lineStartX = x + Math.cos(midAngle) * (element.outerRadius + 5);
      const lineStartY = y + Math.sin(midAngle) * (element.outerRadius + 5);
      const lineEndX = x + Math.cos(midAngle) * (element.outerRadius + 25);
      const lineEndY = y + Math.sin(midAngle) * (element.outerRadius + 25);
      ctx.moveTo(lineStartX, lineStartY);
      ctx.lineTo(lineEndX, lineEndY);
      ctx.stroke();
    });
  }
};

interface ChartSeries {
  name: string;
  data: number[];
  stack: string;
}

interface UniversalChartData {
  type: 'pie' | 'bar' | 'column' | 'stackedColumn' | 'line';
  title?: string;
  subtitle?: string;
  categories: string[];
  series: ChartSeries[];
  showLegend?: boolean;
  colors?: string[];
  showDataLabels?: boolean;
  labelsMode?: 'names' | 'values' | 'percentages' | 'names-percentages' | 'none';
  stacking?: 'normal';
  xAxisTitle?: string;
  yAxisTitle?: string;
}

interface PieChartProps {
  data: UniversalChartData;
  showDataLabels?: boolean;
  labelsMode?: 'names' | 'values' | 'percentages' | 'names-percentages' | 'none';
  containerHeight?: number;
}

interface ProcessedDataPoint {
  name: string;
  value: number;
  displayValue: number;
  percentage?: number;
  category?: string;
  stack?: string;
  legend?: string;
}

const sortProcessedData = (processedData: ProcessedDataPoint[]): ProcessedDataPoint[] => {
  return [...processedData].sort((a, b) => b.value - a.value);
};

const PieChart: React.FC<PieChartProps> = ({ 
  data, 
  showDataLabels = false,
  labelsMode = 'names-percentages',
  containerHeight: propContainerHeight
}) => {
  const [chartData, setChartData] = useState<ProcessedDataPoint[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showAllLegends, setShowAllLegends] = useState(false);
  const [itemsPerRow, setItemsPerRow] = useState(3);
  const [selectedSeries, setSelectedSeries] = useState<Set<number>>(new Set());

  const colors = [
  "#53eafd",
  "#ffdf20",
  "#bbf451",
  "#ffa2a2",
  "#c4b4ff",
  "#8ec5ff",
  "#ffb86a",
  "#7bf1a8",
  "#fda5d5",
  "#46ecd5",
  "#ffd230",
  "#a3b3ff",
  "#ffa1ad",
  "#5ee9b5",
  "#dab2ff",
  "#74d4ff",
  "#f4a8ff"
  ];

  const formatValue = (value: number): string => {
    if (value >= 1000000000) {
      return (value / 1000000000).toFixed(1) + 'B';
    } else if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
    }
    return Math.round(value).toString();
  };

  const calculateItemsPerRow = (data: ProcessedDataPoint[]) => {
    const container = document.querySelector('#legend-container-pie') as HTMLElement;
    if (!container || data.length === 0) {
      setItemsPerRow(3);
      return;
    }

    const tempContainer = document.createElement('div');
    tempContainer.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-family: Inter;
      font-size: 13px;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: 0.32px;
    `;
    document.body.appendChild(tempContainer);
    
    let itemsThatFit = 0;
    const containerWidth = container.offsetWidth;
    const total = data.reduce((sum, d) => sum + d.value, 0);
    
    // First, measure the "All" button width
    const allButton = document.createElement('div');
    allButton.style.position = 'absolute';
    allButton.style.visibility = 'hidden';
    allButton.style.whiteSpace = 'nowrap';
    allButton.style.fontFamily = 'Inter';
    allButton.style.fontWeight = '500';
    allButton.style.fontSize = '13px';
    allButton.style.lineHeight = '18px';
    allButton.style.letterSpacing = '0.32px';
    allButton.textContent = 'Select All';
    document.body.appendChild(allButton);
    const allButtonWidth = allButton.offsetWidth + 15; // 15px for gap
    document.body.removeChild(allButton);
    
    // Reserve space for "See More" button (assuming it will be needed)
    const seeMoreButton = document.createElement('div');
    seeMoreButton.style.position = 'absolute';
    seeMoreButton.style.visibility = 'hidden';
    seeMoreButton.style.whiteSpace = 'nowrap';
    seeMoreButton.style.fontFamily = 'Inter';
    seeMoreButton.style.fontWeight = '400';
    seeMoreButton.style.fontSize = '13px';
    seeMoreButton.style.lineHeight = '18px';
    seeMoreButton.style.letterSpacing = '0.32px';
    seeMoreButton.textContent = 'See More';
    document.body.appendChild(seeMoreButton);
    const seeMoreButtonWidth = seeMoreButton.offsetWidth + 15; // 15px for gap
    document.body.removeChild(seeMoreButton);
    
    // Available width for legend items (reserve space for buttons)
    const availableWidth = containerWidth - allButtonWidth - seeMoreButtonWidth;
    
    // If we have many series, use a more conservative approach
    if (data.length > 10) {
      // Assume average width per item is around 120px for long labels
      const averageItemWidth = 120;
      const maxItems = Math.floor(availableWidth / averageItemWidth);
      setItemsPerRow(Math.max(1, maxItems - 1)); // Leave extra space
      return;
    }
    
    // Measure actual legend items
    data.forEach((item, index) => {
      const testItem = document.createElement('div');
      testItem.style.position = 'absolute';
      testItem.style.visibility = 'hidden';
      testItem.style.whiteSpace = 'nowrap';
      testItem.style.fontFamily = 'Inter';
      testItem.style.fontWeight = '400';
      testItem.style.fontSize = '13px';
      testItem.style.lineHeight = '18px';
      testItem.style.letterSpacing = '0.32px';
      testItem.textContent = `${item.name}: ${formatValue(item.value)} (${((item.value / total) * 100).toFixed(1)}%)`;
      document.body.appendChild(testItem);
      
      const itemWidth = testItem.offsetWidth + 15; // 15px gap
      document.body.removeChild(testItem);
      
      if (itemsThatFit * itemWidth <= availableWidth) {
        itemsThatFit++;
      }
    });
    
    setItemsPerRow(Math.max(1, itemsThatFit));
  };

  // Toggle series selection (multi-select style)
  const toggleSeriesSelection = (seriesIndex: number) => {
    const newSelectedSeries = new Set(selectedSeries);
    if (newSelectedSeries.has(seriesIndex)) {
      newSelectedSeries.delete(seriesIndex); // Deselect this series
    } else {
      newSelectedSeries.add(seriesIndex); // Select this series
    }
    setSelectedSeries(newSelectedSeries);
  };

  // Select all series
  const selectAllSeries = () => {
    const allSeries = new Set(chartData.map((_, index) => index));
    setSelectedSeries(allSeries);
  };

  // Memoize the processed and sorted data
  const processedData = useMemo(() => {
    try {
      if (!data || !data.series || !Array.isArray(data.series) || data.series.length === 0) {
        return [];
      }

      if (!Array.isArray(data.categories)) {
        return [];
      }

      let rawProcessedData: ProcessedDataPoint[] = [];

      // Check if this is data converted from bar/column chart (has stacking or multiple series with all zero values in main stack)
      const isConvertedFromBarChart = data.stacking === 'normal' || 
        (data.series.length > 1 && data.series.some(s => s.stack === 'secondary'));

      if (isConvertedFromBarChart || data.type === 'line') {
        // Handle data converted from bar/column charts
        // Combine all series data by summing values across categories
        const categoryTotals: { [key: string]: number } = {};
        
        data.series.forEach((series) => {
          series.data.forEach((value, index) => {
            const category = data.categories[index];
            if (category && value > 0) {
              categoryTotals[category] = (categoryTotals[category] || 0) + value;
            }
          });
        });

        rawProcessedData = Object.entries(categoryTotals)
          .filter(([_, value]) => value > 0)
          .map(([category, value]) => ({
            name: category,
            value: value,
            displayValue: value,
            category: category
          }));

      } else {
        // Handle regular pie chart data (original format)
        // For pie charts, we typically use the first series
        const primarySeries = data.series[0];
        
        if (primarySeries && primarySeries.data) {
          rawProcessedData = data.categories.map((category, index) => {
            const value = primarySeries.data[index] || 0;
            
            return {
              name: category,
              value: value,
              displayValue: value,
              category: category
            };
          }).filter(item => item.value > 0); // Filter out zero values
        }
      }

      // Sort the data by value (largest first)
      return sortProcessedData(rawProcessedData);
    } catch (err) {
      console.error('Error processing pie chart data:', err);
      return [];
    }
  }, [data]);

  useEffect(() => {
    setError(null);
    
    if (processedData.length === 0) {
      if (data && data.series && data.series.length > 0) {
        setError('No valid data points found');
      }
      setChartData([]);
    } else {
      setChartData(processedData);
    }
  }, [processedData, data]);

  // Calculate items per row when chartData changes
  useEffect(() => {
    if (chartData.length > 0) {
      setTimeout(() => {
        calculateItemsPerRow(chartData);
      }, 100);
    }
  }, [chartData]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      if (chartData.length > 0) {
        calculateItemsPerRow(chartData);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [chartData]);


  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  if (!data || !data.series || data.series.length === 0) {
    return <div className="text-center p-4 text-gray-500 pt-36 pb-36">No data available for chart</div>;
  }

  if (!chartData.length) {
    return <div className="text-center p-4 text-gray-500 pt-36 pb-36">No data available for chart</div>;
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const createDataLabelsConfig = () => {
    if (!showDataLabels || labelsMode === 'none') {
      return { display: false };
    }

    // For external labels, we'll use the custom plugin instead of datalabels
    if (labelsMode === 'names-percentages' || labelsMode === 'percentages') {
      // Register and use external labels plugin
      if (!ChartJS.registry.plugins.get('externalLabels')) {
        ChartJS.register(ExternalLabelsPlugin);
      }
      return { display: false }; // Disable default datalabels when using external
    }

    const baseConfig = {
      color: '#323f4a',
      font: {
        size: 10
      },
      padding: 2,
      textAlign: 'center' as const,
      backgroundColor: 'transparent',
      display: function(context: any) {
        const value = context.dataset.data[context.dataIndex];
        // Recalculate total from the dataset
        const datasetTotal = context.dataset.data.reduce((sum: number, val: any) => {
          const numValue = typeof val === 'number' ? val : 0;
          return sum + numValue;
        }, 0);
        const percentage = (value / datasetTotal) * 100;
        return percentage > 10;
      }
    };

    switch (labelsMode) {
      case 'names':
        return {
          ...baseConfig,
          formatter: (value: number, context: any) => {
            const datasetTotal = context.dataset.data.reduce((sum: number, val: any) => {
              const numValue = typeof val === 'number' ? val : 0;
              return sum + numValue;
            }, 0);
            const percentage = (value / datasetTotal) * 100;
            if (percentage <= 10) return '';
            return context.chart.data.labels[context.dataIndex];
          }
        };

      case 'values':
        return {
          ...baseConfig,
          formatter: (value: number, context: any) => {
            const percentage = (value / total) * 100;
            if (percentage <= 10) return '';
            return formatValue(value);
          }
        };

      default:
        return { display: false };
    }
  };

  // Filter chart data based on selected series (show only selected items)
  // If nothing is selected, show all (default state)
  const filteredChartData = selectedSeries.size > 0 
    ? chartData.filter((_, index) => selectedSeries.has(index))
    : chartData; // Show all if none selected
  
  const chartJSData: ChartData<'pie'> = {
    labels: filteredChartData.map(item => item.name),
    datasets: [{
      data: filteredChartData.map(item => item.value),
      backgroundColor: filteredChartData.map((_, index) => {
        const originalIndex = chartData.findIndex(item => item.name === filteredChartData[index].name);
        return colors[originalIndex % colors.length] + '80';
      }),
      borderColor: filteredChartData.map((_, index) => {
        const originalIndex = chartData.findIndex(item => item.name === filteredChartData[index].name);
        return colors[originalIndex % colors.length];
      }),
      borderWidth: 1
    }]
  };

  const options: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 45,
        left: 0,
        right: 0,
        top: 45
      }
    },
    plugins: {
      legend: {
        display: false, // Hide built-in legend, but keep it enabled for HTML legend plugin
        labels: {
          generateLabels: function(chart: any) {
            const chartData = chart.data;
            if (chartData.labels && chartData.datasets.length) {
              const chartTotal = chartData.datasets[0].data.reduce((sum: number, value: any) => {
                const numValue = typeof value === 'number' ? value : 0;
                return sum + numValue;
              }, 0);
              
              return chartData.labels.map((label: any, i: number) => {
                const value = chartData.datasets[0].data[i] as number;
                const percentage = ((value / chartTotal) * 100).toFixed(1);
                const formattedValue = formatValue(value);
                const backgroundColor = Array.isArray(chartData.datasets[0].backgroundColor) 
                  ? chartData.datasets[0].backgroundColor[i] 
                  : chartData.datasets[0].backgroundColor;
                const borderColor = Array.isArray(chartData.datasets[0].borderColor) 
                  ? chartData.datasets[0].borderColor[i] 
                  : chartData.datasets[0].borderColor;
                
                return {
                  text: `${label}: ${formattedValue} (${percentage}%)`,
                  fillStyle: backgroundColor as string,
                  strokeStyle: borderColor as string,
                  fontColor: '#323f4a',
                  lineWidth: 1,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      title: {
        display: false, // Disable built-in title, use HTML title instead
      },
      tooltip: {
        displayColors: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        usePointStyle: true, // This forces circles instead of squares
        callbacks: {
          title: function(context: any[]) {
            return context[0]?.label || '';
          },
          label: function(context: any) {
            const value = context.parsed as number;
            const chartTotal = context.chart.data.datasets[0].data.reduce((sum: number, val: any) => {
              const numValue = typeof val === 'number' ? val : 0;
              return sum + numValue;
            }, 0);
            const percentage = ((value / chartTotal) * 100).toFixed(1);
            const formattedValue = formatValue(value);
            return `${formattedValue} (${percentage}%)`;
          },
          labelColor: function(context: any) {
            // Return the correct color for the tooltip circle (filled)
            const dataIndex = context.dataIndex;
            const backgroundColor = Array.isArray(context.chart.data.datasets[0].backgroundColor) 
              ? context.chart.data.datasets[0].backgroundColor[dataIndex] 
              : context.chart.data.datasets[0].backgroundColor;
            const borderColor = Array.isArray(context.chart.data.datasets[0].borderColor) 
              ? context.chart.data.datasets[0].borderColor[dataIndex] 
              : context.chart.data.datasets[0].borderColor;
            
            // Remove transparency from backgroundColor for solid fill
            const solidColor = backgroundColor.toString().replace(/80$/, '');
            
            return {
              borderColor: borderColor as string,
              backgroundColor: solidColor // Fill the entire circle with legend color
            };
          },
          afterLabel: function(context: any) {
            return context.label;
          }
        }
      },
      datalabels: createDataLabelsConfig()
    }
  };

  return (
    <>
      <div className="w-full">
        {/* Chart Title */}
        {/* {data.title && (
          <div className="text-h4 text-primary-text mt-2 mb-4 text-left">{data.title}</div>
        )} */}
        {/* React Legend Container - Top */}
        {data.showLegend !== false && (
          <div id="legend-container-pie" className="mb-4">
            <ul key={showAllLegends ? 'expanded' : 'collapsed'} className="flex" style={{ 
              flexWrap: showAllLegends ? 'wrap' : 'nowrap', 
              overflow: showAllLegends ? 'visible' : 'hidden', 
              gap: '8px' 
            }}>
              {(() => {
                const itemsToShow = showAllLegends ? chartData.length : itemsPerRow;
                const hasMoreItems = chartData.length > itemsPerRow;
                const hasSelectedItems = selectedSeries.size > 0;
                const allItemsSelected = selectedSeries.size === chartData.length;
                const shouldShowSelectAll = hasSelectedItems && !allItemsSelected;
                // If nothing is selected, treat all as selected (default state)
                const effectiveSelectedSeries = selectedSeries.size > 0 ? selectedSeries : new Set(chartData.map((_, index) => index));
                
                return (
                  <>
                    {/* "Select All" option - only show when some (but not all) items are selected */}
                    {shouldShowSelectAll && (
                      <li 
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={selectAllSeries}
                      >
                        <span 
                          className="w-4 h-4 rounded-full flex-shrink-0 border-2"
                          style={{ 
                            backgroundColor: 'transparent',
                            borderColor: '#4F46E5',
                            borderRadius: '50%'
                          }}
                        />
                        <span 
                          className="text-sm font-medium whitespace-nowrap"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: '500',
                            fontSize: '13px',
                            lineHeight: '18px',
                            letterSpacing: '0.32px',
                            color: '#4F46E5'
                          }}
                        >
                          Select All
                        </span>
                      </li>
                    )}
                    
                    {chartData.slice(0, itemsToShow).map((item, index) => (
                      <li 
                        key={index} 
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={() => toggleSeriesSelection(index)}
                      >
                        <span 
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ 
                            backgroundColor: colors[index % colors.length],
                            borderRadius: '50%',
                            opacity: effectiveSelectedSeries.has(index) ? 1 : 0.5
                          }}
                        />
                        <span 
                          className="text-sm whitespace-nowrap"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: '400',
                            fontSize: '13px',
                            lineHeight: '18px',
                            letterSpacing: '0.32px',
                            color: effectiveSelectedSeries.has(index) ? '#323f4a' : '#aaa',
                            textDecoration: 'none'
                          }}
                        >
                          {item.name}: {formatValue(item.value)} ({((item.value / total) * 100).toFixed(1)}%)
                        </span>
                      </li>
                    ))}
                    {hasMoreItems && !showAllLegends && (
                      <li 
                        className="flex items-center gap-1 cursor-pointer px-2 rounded-full text-sm transition-colors"
                        style={{
                          color: '#6b7280',
                          fontFamily: 'Inter',
                          backgroundColor: '#f3f4f6',
                          border: 'none'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowAllLegends(true)}
                      >
                        <span>See more ({chartData.length - itemsPerRow})</span>
                        <span style={{ fontSize: '10px' }}>▼</span>
                      </li>
                    )}
                    {hasMoreItems && showAllLegends && (
                      <li 
                        className="flex items-center gap-1 cursor-pointer px-2 rounded-full text-sm transition-colors"
                        style={{
                          color: '#6b7280',
                          fontFamily: 'Inter',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowAllLegends(false)}
                      >
                        <span>See less</span>
                        <span style={{ fontSize: '10px' }}>▲</span>
                      </li>
                    )}
                  </>
                );
              })()}
            </ul>
          </div>
        )}
        {/* Chart Container - Full width */}
        <div style={{ width: '100%', height: `${propContainerHeight || 400}px` }}>
          <Pie 
            data={chartJSData} 
            options={options}
          />
        </div>
      </div>
    </>
  );
};

export default PieChart;